import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { type Board, boardOwner, boardQueryOptions, type Column } from '@/entities/board'
import { type Company, companyListQueryOptions } from '@/entities/company'
import { type Employee, employeesQueryOptions, meQueryOptions } from '@/entities/employee'
import { type Project, projectListQueryOptions } from '@/entities/project'
import { boardTasksQueryOptions, type Task } from '@/entities/task'
import { runningQueryOptions } from '@/entities/time-entry'
import { CreateTaskDialog } from '@/features/create-task'
import { BoardMenu } from '@/features/manage-board'
import { AddColumnButton } from '@/features/manage-columns'
import { BoardTitle } from '@/features/rename-board'
import {
  BoardCustomerVisibilityToggle,
  BulkCustomerVisibilityDialog,
} from '@/features/set-customer-visibility'
import { ApiError } from '@/shared/api'
import { cn } from '@/shared/lib'
import { Avatar } from '@/shared/ui/avatar'
import { Button } from '@/shared/ui/button'
import { DepartmentBadges } from '@/shared/ui/department-badge'
import { Icon } from '@/shared/ui/icon'
import { Menu, MenuItem } from '@/shared/ui/menu'
import { StatusScreen } from '@/shared/ui/status-screen'
import { ZoneTag } from '@/shared/ui/zone-badge'
import { BoardView } from '@/widgets/board-view'

interface BoardSearch {
  mine?: boolean
  type?: 'task' | 'bug'
  // Мультифильтр по исполнителям: id сотрудников + литерал 'none' («не назначено»).
  assignee?: string[]
  // Только задачи, открытые клиенту: «что видит заказчик» — отдельный взгляд на доску.
  customer?: boolean
}

// Санитайз массива assignee из URL (строка/массив → string[], пустые отброшены).
function parseAssignee(raw: unknown): string[] {
  const arr = Array.isArray(raw) ? raw : raw != null ? [raw] : []
  return arr.filter((v): v is string => typeof v === 'string' && v !== '')
}

export const Route = createFileRoute('/boards_/$boardId')({
  component: BoardPage,
  head: () => ({ meta: [{ title: 'Доска · STIT Task' }] }),
  // Фильтры доски живут в URL (шарятся, переживают перезагрузку). Пустые значения
  // в схему не пишем — URL чистый, когда фильтр выключен.
  validateSearch: (s: Record<string, unknown>): BoardSearch => {
    const assignee = parseAssignee(s.assignee)
    return {
      ...(s.mine === true || s.mine === 'true' ? { mine: true } : {}),
      ...(s.type === 'task' || s.type === 'bug' ? { type: s.type } : {}),
      ...(assignee.length > 0 ? { assignee } : {}),
      ...(s.customer === true || s.customer === 'true' ? { customer: true } : {}),
    }
  },
})

// Дропдаун-чип мультивыбора исполнителей + «Не назначено». Клиентская фильтрация;
// сотрудники с задачами на доске — вперёд. Меню не закрывается по выбору (мультивыбор).
function AssigneeFilter({
  employees,
  boardAssigneeIds,
  selected,
  onToggle,
  onClear,
}: {
  employees: Employee[]
  boardAssigneeIds: Set<string>
  selected: string[]
  onToggle: (id: string) => void
  onClear: () => void
}) {
  const count = selected.length
  const ordered = [
    ...employees.filter((e) => boardAssigneeIds.has(e.id)),
    ...employees.filter((e) => !boardAssigneeIds.has(e.id)),
  ]
  return (
    <Menu
      align="start"
      caret
      ariaLabel="Фильтр по исполнителям"
      trigger={
        <span className="font-mono text-xs uppercase tracking-wide">
          Исполнители{count > 0 && ` · ${count}`}
        </span>
      }
      triggerClassName={cn(
        'border px-3 py-1',
        count > 0
          ? 'border-border-strong bg-bg-elevated text-text-primary'
          : 'border-border-default text-text-secondary',
      )}
    >
      {() => (
        <>
          <MenuItem selected={selected.includes('none')} onSelect={() => onToggle('none')}>
            Не назначено
          </MenuItem>
          <div className="my-1 h-px bg-border-subtle" aria-hidden />
          {ordered.map((e) => (
            <MenuItem key={e.id} selected={selected.includes(e.id)} onSelect={() => onToggle(e.id)}>
              <span className="inline-flex min-w-0 items-center gap-2">
                <Avatar name={e.name} size="sm" />
                <span className="truncate">{e.name}</span>
              </span>
            </MenuItem>
          ))}
          {count > 0 && (
            <>
              <div className="my-1 h-px bg-border-subtle" aria-hidden />
              <button
                type="button"
                data-menu-item
                onClick={onClear}
                className="rounded-control px-2.5 py-2 text-left text-sm text-text-tertiary transition-colors hover:bg-bg-raised hover:text-text-primary"
              >
                Сбросить
              </button>
            </>
          )}
        </>
      )}
    </Menu>
  )
}

const TYPE_OPTIONS: { value: 'all' | 'task' | 'bug'; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'task', label: 'Задачи' },
  { value: 'bug', label: 'Баги' },
]

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'rounded-pill border px-3 py-1 font-mono text-xs uppercase tracking-wide transition-colors',
        active
          ? 'border-border-strong bg-bg-elevated text-text-primary'
          : 'border-border-default text-text-secondary hover:text-text-primary',
      )}
    >
      {children}
    </button>
  )
}

const SKELETON_COLS = ['s1', 's2', 's3', 's4']
const SKELETON_CARDS = ['c1', 'c2', 'c3']

function CardsSkeleton() {
  return (
    <>
      {SKELETON_CARDS.map((c) => (
        <div key={c} className="h-24 shrink-0 animate-pulse rounded-card bg-bg-elevated" />
      ))}
    </>
  )
}

// Скелет доски: реальные заголовки колонок (если уже загружены) + пульс-карточки,
// иначе — четыре обобщённые колонки.
function BoardSkeleton({ columns }: { columns?: Column[] }) {
  const cols = columns ?? SKELETON_COLS.map((id) => ({ id, name: '' }))
  return (
    // overflow-x-auto, а не hidden: колонки шире экрана телефона, и скрытый
    // overflow просто отрезал бы их вместе со скроллом. Ширина — как у реальной
    // колонки, иначе на переходе «скелет → доска» раскладка прыгает.
    <div className="flex h-full gap-4 overflow-x-auto px-4 pt-2 pb-4 sm:px-6">
      {cols.map((c) => (
        <div key={c.id} className="flex w-[86vw] shrink-0 flex-col gap-3 sm:w-[300px]">
          {c.name ? (
            <div className="mb-1 px-1 text-sm font-medium text-text-primary">{c.name}</div>
          ) : (
            <div className="mb-1 h-5 w-24 animate-pulse rounded-control bg-bg-elevated" />
          )}
          <CardsSkeleton />
        </div>
      ))}
    </div>
  )
}

// BoardBreadcrumb — контур доски над её названием: «компания → проект» у
// проектной, одна компания у доски компании, владелец у личной. Понять, чей это
// контур, нужно с первого взгляда; компания кликабельна — её страница и есть
// место, где живут проекты и доски компании.
function BoardBreadcrumb({
  board,
  project,
  companies,
  employees,
}: {
  board: Board
  project: Project | undefined
  companies: Company[] | undefined
  employees: Employee[] | undefined
}) {
  const owner = boardOwner(board)
  const companyId = owner.kind === 'company' ? owner.id : project?.companyId
  const company = companies?.find((c) => c.id === companyId)
  const holder = owner.kind === 'employee' ? employees?.find((e) => e.id === owner.id) : undefined

  if (owner.kind === 'employee') {
    return (
      <div className="mb-1 flex flex-wrap items-center gap-1.5 text-xs text-text-secondary">
        <Link to="/employees" className="transition-colors hover:text-text-primary">
          Сотрудники
        </Link>
        <span aria-hidden>/</span>
        <span>{holder ? `Личная доска: ${holder.name}` : 'Личная доска'}</span>
      </div>
    )
  }

  if (!company && !project) return null
  return (
    <div className="mb-1 flex flex-wrap items-center gap-1.5 text-xs text-text-secondary">
      {company ? (
        <Link
          to="/companies/$companyId"
          params={{ companyId: company.id }}
          className="transition-colors hover:text-text-primary"
        >
          {company.name}
        </Link>
      ) : null}
      {company && project ? <span aria-hidden>/</span> : null}
      {project ? <span>{project.name}</span> : null}
    </div>
  )
}

function BoardPage() {
  const { boardId } = Route.useParams()
  const boardQ = useQuery(boardQueryOptions(boardId))
  const tasksQ = useQuery(boardTasksQueryOptions(boardId))
  const employeesQ = useQuery(employeesQueryOptions)
  const meQ = useQuery(meQueryOptions)
  const runningQ = useQuery(runningQueryOptions)
  // Доска знает только project_id — имена проекта и компании собираем из уже
  // загруженных справочников, отдельного запроса на это не заводим.
  const projectsQ = useQuery(projectListQueryOptions(true, true))
  const companiesQ = useQuery(companyListQueryOptions(true, true))
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  // Колонка, в которую создаём: кнопка колонки задаёт её, кнопка шапки — нет
  // (там диалог сам подставит первую).
  const [createIn, setCreateIn] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [visibilityOpen, setVisibilityOpen] = useState(false)

  const assignee = search.assignee ?? []

  // Пересобираем весь search (exactOptional запрещает undefined-как-удаление):
  // выключенный фильтр просто не попадает в объект → чистый URL.
  const setSearch = (next: BoardSearch) => void navigate({ search: next })
  const buildSearch = (parts: {
    mine?: boolean | undefined
    type?: 'task' | 'bug' | undefined
    assignee?: string[] | undefined
    customer?: boolean | undefined
  }): BoardSearch => ({
    ...(parts.mine ? { mine: true } : {}),
    ...(parts.type ? { type: parts.type } : {}),
    ...(parts.assignee && parts.assignee.length > 0 ? { assignee: parts.assignee } : {}),
    ...(parts.customer ? { customer: true } : {}),
  })
  const rest = { type: search.type, assignee, customer: search.customer }
  const toggleMine = () => setSearch(buildSearch({ ...rest, mine: !search.mine }))
  const pickType = (t: 'all' | 'task' | 'bug') =>
    setSearch(buildSearch({ ...rest, mine: search.mine, type: t === 'all' ? undefined : t }))
  const toggleCustomer = () =>
    setSearch(buildSearch({ ...rest, mine: search.mine, customer: !search.customer }))
  const toggleAssignee = (id: string) => {
    const set = new Set(assignee)
    if (set.has(id)) set.delete(id)
    else set.add(id)
    setSearch(buildSearch({ ...rest, mine: search.mine, assignee: [...set] }))
  }
  const clearAssignee = () =>
    setSearch(buildSearch({ ...rest, mine: search.mine, assignee: undefined }))

  if (boardQ.isPending) {
    return (
      <div className="flex h-full flex-col">
        <div className="shrink-0 px-4 pt-8 sm:px-6">
          <div className="mb-6 h-8 w-40 animate-pulse rounded-control bg-bg-elevated" />
        </div>
        <div className="min-h-0 flex-1">
          <BoardSkeleton />
        </div>
      </div>
    )
  }

  if (boardQ.isError) {
    const notFound = boardQ.error instanceof ApiError && boardQ.error.status === 404
    return (
      <StatusScreen
        title={notFound ? 'Доска не найдена' : 'Не удалось загрузить доску'}
        message={
          notFound
            ? 'Возможно, она удалена или у вас нет доступа.'
            : 'Попробуйте обновить страницу.'
        }
      >
        {!notFound && (
          <Button
            variant="secondary"
            onClick={() => {
              void boardQ.refetch()
            }}
          >
            Повторить
          </Button>
        )}
        <Link
          to="/companies"
          className="inline-flex items-center rounded-control px-5 py-3 text-sm text-text-secondary transition-colors hover:text-text-primary"
        >
          К компаниям
        </Link>
      </StatusScreen>
    )
  }

  const board = boardQ.data
  const meId = meQ.data?.id
  const project = projectsQ.data?.find((p) => p.id === board.projectId)
  const company = companiesQ.data?.find((c) => c.id === project?.companyId)
  const running = (runningQ.data ?? []).map((r) => ({
    taskId: r.taskId,
    startedAt: r.startedAt,
    taskTitle: r.taskTitle,
  }))

  const boardAssigneeIds = new Set(
    (tasksQ.data ?? []).map((t) => t.assigneeId).filter((id): id is string => Boolean(id)),
  )
  // 'none' совпадает с не назначенными; иначе assigneeId должен быть в выборке.
  const matchesAssignee = (t: Task) =>
    assignee.length === 0 ||
    (t.assigneeId ? assignee.includes(t.assigneeId) : assignee.includes('none'))

  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0 px-4 pt-8 sm:px-6">
        <header className="mb-6 flex flex-col">
          <BoardBreadcrumb
            board={board}
            project={project}
            companies={companiesQ.data}
            employees={employeesQ.data}
          />
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <DepartmentBadges names={board.departments} />
            {/* Сроки на плитках доски — колонка моментов; зона названа один раз
                в шапке доски, а не тегом на каждой карточке. */}
            <ZoneTag kind="org" prefix="сроки" />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
            <BoardTitle board={board} />
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to="/gantt/$boardId"
                params={{ boardId: board.id }}
                className="inline-flex items-center justify-center gap-2 rounded-control border border-border-strong px-3 pt-[9px] pb-[7px] font-mono text-xs font-medium uppercase tracking-wide text-text-primary transition-colors hover:border-text-primary hover:bg-bg-elevated"
              >
                <Icon name="gantt" size={14} />
                Гант
              </Link>
              <AddColumnButton boardId={board.id} columnCount={board.columns.length} />
              {/* Видимость доски — первая половина гейта, поэтому стоит рядом с
                  массовой пометкой: закрытая доска обесценивает любые пометки. */}
              <BoardCustomerVisibilityToggle board={board} />
              {/* Массовая пометка — там же, где остальные операции доски: открыть
                  клиенту спринт целиком, не заходя в каждую задачу. */}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setVisibilityOpen(true)}
                disabled={(tasksQ.data ?? []).length === 0}
              >
                <Icon name="eye" size={14} />
                Видимость
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setCreateIn(null)
                  setCreateOpen(true)
                }}
              >
                <Icon name="plus" size={14} />
                Задача
              </Button>
              <BoardMenu board={board} />
            </div>
          </div>
        </header>
        {/* Разделители скрыты на узком экране: ряд там переносится, и осиротевшая
            палочка встаёт в начало строки как мусор. */}
        <div className="flex flex-wrap items-center gap-2 pb-1">
          <FilterChip active={search.mine ?? false} onClick={toggleMine}>
            Мои
          </FilterChip>
          <span aria-hidden className="mx-1 hidden h-4 w-px bg-border-subtle sm:block" />
          {TYPE_OPTIONS.map((o) => (
            <FilterChip
              key={o.value}
              active={(search.type ?? 'all') === o.value}
              onClick={() => pickType(o.value)}
            >
              {o.label}
            </FilterChip>
          ))}
          <span aria-hidden className="mx-1 hidden h-4 w-px bg-border-subtle sm:block" />
          <AssigneeFilter
            employees={employeesQ.data ?? []}
            boardAssigneeIds={boardAssigneeIds}
            selected={assignee}
            onToggle={toggleAssignee}
            onClear={clearAssignee}
          />
          <span aria-hidden className="mx-1 hidden h-4 w-px bg-border-subtle sm:block" />
          {/* «Что видит клиент» — тот же взгляд на доску, только по пометке. */}
          <FilterChip active={search.customer ?? false} onClick={toggleCustomer}>
            Видно клиенту
          </FilterChip>
        </div>
      </div>
      <div className="min-h-0 flex-1">
        {tasksQ.isPending ? (
          <BoardSkeleton columns={board.columns} />
        ) : tasksQ.isError ? (
          <div className="flex items-center gap-3 px-4 py-6 text-sm text-text-tertiary sm:px-6">
            Не удалось загрузить задачи доски
            <button
              type="button"
              onClick={() => {
                void tasksQ.refetch()
              }}
              className="text-text-secondary underline transition-colors hover:text-text-primary"
            >
              Повторить
            </button>
          </div>
        ) : (
          <BoardView
            board={board}
            tasks={tasksQ.data.filter(
              (t) =>
                (!search.mine || t.assigneeId === meId) &&
                (!search.type || t.type === search.type) &&
                (!search.customer || t.customerVisible) &&
                matchesAssignee(t),
            )}
            employees={employeesQ.data ?? []}
            running={running}
            meId={meId}
            onCreate={(columnId) => {
              setCreateIn(columnId)
              setCreateOpen(true)
            }}
          />
        )}
      </div>
      {createOpen && (
        <CreateTaskDialog
          boards={[board]}
          employees={employeesQ.data ?? []}
          {...(createIn ? { defaultColumnId: createIn } : {})}
          onClose={() => setCreateOpen(false)}
        />
      )}
      {visibilityOpen && (
        <BulkCustomerVisibilityDialog
          board={board}
          tasks={tasksQ.data ?? []}
          columns={board.columns}
          binding={{ companyId: project?.companyId ?? null, projectId: board.projectId || null }}
          companyName={company?.name}
          onClose={() => setVisibilityOpen(false)}
        />
      )}
    </div>
  )
}
